import Cookies from 'js-cookie';
import appSettings from '../config/settings';

/**
 * Browser-side twin of `server/api-util/sessionCookie.js`.
 *
 * All cookie state the server needs to read lives in a single `__session` cookie,
 * because Firebase Hosting strips every other cookie from requests it proxies to
 * the backend. See the server module for the full rationale.
 *
 * NOTE: keep the envelope format in sync with `server/api-util/sessionCookie.js`.
 */

const SESSION_COOKIE_NAME = '__session';

// Matches the server-side expiration so the session doesn't expire earlier on one
// side than the other.
const EXPIRATION_DAYS = 180;

const cookieOptions = () => ({
  path: '/',
  expires: EXPIRATION_DAYS,
  sameSite: 'lax',
  ...(appSettings.usingSSL ? { secure: true } : {}),
});

/**
 * Read the whole envelope.
 *
 * @returns {Object} the parsed envelope, or an empty object
 */
export const readEnvelope = () => {
  const raw = Cookies.get(SESSION_COOKIE_NAME);

  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (e) {
    // A malformed envelope is treated as absent rather than fatal.
    return {};
  }
};

/**
 * Read a single namespaced block from the envelope.
 *
 * @param {String} key envelope key, e.g. 'token'
 * @returns {*} the stored value, or null
 */
export const readKey = key => {
  const value = readEnvelope()[key];
  return value === undefined ? null : value;
};

/**
 * Set a single namespaced block, leaving the rest of the envelope untouched.
 *
 * @param {String} key envelope key, e.g. 'token'
 * @param {*} value value to store
 */
export const writeKey = (key, value) => {
  const next = { ...readEnvelope(), [key]: value };
  Cookies.set(SESSION_COOKIE_NAME, JSON.stringify(next), cookieOptions());
};

/**
 * Remove a single namespaced block, leaving the rest of the envelope untouched.
 * If the envelope ends up empty, the cookie itself is removed.
 *
 * @param {String} key envelope key, e.g. 'token'
 */
export const clearKey = key => {
  const current = readEnvelope();
  delete current[key];

  if (Object.keys(current).length === 0) {
    Cookies.remove(SESSION_COOKIE_NAME, { path: '/' });
    return;
  }

  Cookies.set(SESSION_COOKIE_NAME, JSON.stringify(current), cookieOptions());
};

/**
 * A Sharetribe SDK token store backed by the `__session` envelope.
 *
 * Implements the SDK's token store interface (getToken / setToken / removeToken),
 * mirroring the SDK's own `browserCookieStore`. Passed explicitly to
 * `createInstance` in `src/index.js` — without it the SDK silently builds its own
 * store using the hardcoded `st-<clientId>-token` name, which Firebase strips.
 *
 * @returns {Object} token store
 */
export const sessionBrowserStore = () => {
  const getToken = () => readKey('token');

  const setToken = tokenData => {
    writeKey('token', tokenData);
  };

  const removeToken = () => {
    clearKey('token');
  };

  return { getToken, setToken, removeToken };
};
